// track.ts — production-ready analytics client (no external deps, no `any`)

/* -------------------- Types -------------------- */

export type EventName =
  | "click"
  | "video_play"
  | "scroll_depth"
  | "page_view"
  | "page_end"
  | "session_start"
  | "session_end"
  | "schedule_submit"
  | "slot_selected"
  | "open_quick_actions"
  | "service_more"
  | "cta"
  | (string & {}); // extension-safe

export type Variant = "primary" | "secondary" | "ghost";

export interface TrackEvent {
  // required
  event: EventName;

  // optional user-supplied fields
  label?: string;
  testomonialId?: string | number;
  value?: number;
  component?: string;
  source?: string; // e.g. "web", "email"
  meta?: Readonly<Record<string, unknown>>;
  variant?: Variant;
  testimonial_id?: string | number;
  type?: string;

  // auto-injected context (client fills in)
  sessionId?: string;
  ts?: number;            // epoch ms (client-side time)
  path?: string;
  ref?: string | null;    // document.referrer
  schemaVersion?: number; // bump when you change payload semantics
}

type BatchPayload = {
  events: Readonly<TrackEvent>[];
  ts: number;            // batch enqueue timestamp
  session?: string;      // mirrors sessionId (helps ingestion)
};

//type SendResult = { ok: true } | { ok: false; status: number; error: string };

/* -------------------- Config -------------------- */

export type TrackConfig = {
  endpoint: string;          // "/api/track" or absolute URL
  maxPerInterval: number;    // token bucket capacity
  intervalMs: number;        // token bucket refill cadence
  maxBatch: number;          // events per POST
  maxRetries: number;        // for 429/503/network
  requestTimeoutMs: number;  // per request timeout
  batchDelayMs: number;      // coalesce window
  dedupeWindowMs: number;    // de-dupe identical events
  schemaVersion: number;     // auto-injected into each event
};

const DEFAULTS: TrackConfig = {
  endpoint: "/api/track",
  maxPerInterval: 5,
  intervalMs: 1000,
  maxBatch: 20,
  maxRetries: 5,
  requestTimeoutMs: 5000,
  batchDelayMs: 150,
  dedupeWindowMs: 3000,
  schemaVersion: 1,
};

/* -------------------- Utils -------------------- */

const now = () => Date.now();
const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
const isBrowser = () => typeof window !== "undefined";

function stableHash(
  ev: Omit<TrackEvent, "ts" | "sessionId" | "path" | "ref" | "schemaVersion">
): string {
  // Lightweight, stable enough for client dedupe
  return JSON.stringify(ev);
}

/* -------------------- Token Bucket -------------------- */

class Bucket {
  private capacity: number;
  private intervalMs: number;
  private tokens: number;
  private lastRefill: number;

  constructor(capacity: number, intervalMs: number) {
    this.capacity = capacity;
    this.intervalMs = intervalMs;
    this.tokens = capacity;
    this.lastRefill = now();
  }

  take(): boolean {
    const t = now();
    const elapsed = t - this.lastRefill;
    if (elapsed >= this.intervalMs) {
      const cycles = Math.floor(elapsed / this.intervalMs);
      const toAdd = cycles * this.capacity;
      this.tokens = Math.min(this.capacity, this.tokens + toAdd);
      this.lastRefill = t;
    }
    if (this.tokens > 0) {
      this.tokens -= 1;
      return true;
    }
    return false;
  }
}

/* -------------------- Core Client -------------------- */

class TrackClient {
  private cfg: TrackConfig = { ...DEFAULTS };
  private queue: TrackEvent[] = [];
  private sending = false;
  private bucket = new Bucket(DEFAULTS.maxPerInterval, DEFAULTS.intervalMs);
  private seen = new Map<string, number>(); // dedupe map
  private sessionId: string;
  private utm: Record<string, string> = {};

  constructor() {
    this.sessionId = this.getOrCreateSession();
    this.utm = this.captureUtm();
    if (isBrowser()) {
      window.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") void this.flush(true);
      });
      window.addEventListener("pagehide", () => void this.flush(true));
      window.addEventListener("beforeunload", () => void this.flush(true));
    }
  }

  configure(partial: Partial<TrackConfig>): void {
    this.cfg = { ...this.cfg, ...partial };
    this.bucket = new Bucket(this.cfg.maxPerInterval, this.cfg.intervalMs);
  }

  /** Public enqueue API */
  enqueue(ev: TrackEvent): void {
    if (!isBrowser()) return; // ignore on server

    // de-dupe identical events within a small window (ignoring auto-injected fields)
    const baseForHash: Omit<
      TrackEvent,
      "ts" | "sessionId" | "path" | "ref" | "schemaVersion"
    > = {
      event: ev.event,
      label: ev.label,
      value: Number.isFinite(ev.value ?? NaN) ? ev.value : undefined,
      component: ev.component,
      source: ev.source,
      meta: ev.meta,
      variant: ev.variant,
      testimonial_id: ev.testimonial_id,
      type: ev.type,
    };
    const key = stableHash(baseForHash);
    const last = this.seen.get(key) ?? 0;
    if (now() - last < this.cfg.dedupeWindowMs) return;
    this.seen.set(key, now());

    // auto-inject context
    const utmMeta = Object.keys(this.utm).length > 0 ? this.utm : undefined;
    const enriched: TrackEvent = {
      ...ev,
      value: Number.isFinite(ev.value ?? NaN) ? ev.value : undefined,
      sessionId: this.sessionId,
      ts: now(),
      path: this.safePathname(),
      ref: this.safeReferrer(),
      schemaVersion: this.cfg.schemaVersion,
      ...(utmMeta ? { meta: { ...utmMeta, ...ev.meta } } : {}),
    };

    this.queue.push(enriched);
    if (!this.sending) void this.flushSoon();
  }

  /** Manually flush the queue */
  async flush(forceBeacon = false): Promise<void> {
    if (!this.queue.length) return;
    this.sending = true;
    try {
      while (this.queue.length) {
        if (!forceBeacon && !this.bucket.take()) {
          await sleep(100);
          continue;
        }
        const batch = this.queue.splice(0, this.cfg.maxBatch);
        const ok = forceBeacon ? this.sendBeacon(batch) : await this.sendWithRetry(batch);
        if (!ok) {
          // re-queue and stop trying to avoid tight loops
          this.queue.unshift(...batch);
          break;
        }
      }
    } finally {
      this.sending = false;
    }
  }

  private async flushSoon(): Promise<void> {
    this.sending = true;
    try {
      await sleep(this.cfg.batchDelayMs);
      await this.flush();
    } finally {
      this.sending = false;
    }
  }

  private payload(batch: TrackEvent[]): BatchPayload {
    return { events: batch, ts: now(), session: this.sessionId };
  }

  private sendBeacon(batch: TrackEvent[]): boolean {
    if (!isBrowser() || !("sendBeacon" in navigator)) return false;
    try {
      const blob = new Blob([JSON.stringify(this.payload(batch))], {
        type: "application/json",
      });
      return navigator.sendBeacon(this.cfg.endpoint, blob);
    } catch {
      return false;
    }
  }

  private async sendWithRetry(batch: TrackEvent[]): Promise<boolean> {
    let attempt = 0;
    let delay = 250;

    while (attempt <= this.cfg.maxRetries) {
      attempt += 1;

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), this.cfg.requestTimeoutMs);

      try {
        const resp = await fetch(this.cfg.endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(this.payload(batch)),
          signal: controller.signal,
          keepalive: true,
        });

        clearTimeout(timer);

        if (resp.ok) return true;

        if (resp.status === 429 || resp.status === 503) {
          const ra = resp.headers.get("Retry-After");
          const wait =
            ra && !Number.isNaN(Number(ra))
              ? Math.max(250, Number(ra) * 1000)
              : delay;
          await sleep(wait + Math.floor(Math.random() * 200)); // jitter
          delay = Math.min(delay * 2, 8000);
          continue;
        }

        // non-retryable
        return false;
      } catch {
        clearTimeout(timer);
        // network/timeout
        await sleep(delay + Math.floor(Math.random() * 200));
        delay = Math.min(delay * 2, 8000);
      }
    }
    return false;
  }

  private captureUtm(): Record<string, string> {
    try {
      if (!isBrowser()) return {};
      const stored = sessionStorage.getItem('__trk_utm');
      if (stored) return JSON.parse(stored) as Record<string, string>;
      const params = new URLSearchParams(location.search);
      const utm: Record<string, string> = {};
      for (const key of ['utm_source', 'utm_medium', 'utm_campaign']) {
        const val = params.get(key);
        if (val) utm[key] = val;
      }
      if (Object.keys(utm).length > 0) sessionStorage.setItem('__trk_utm', JSON.stringify(utm));
      return utm;
    } catch {
      return {};
    }
  }

  private getOrCreateSession(): string {
    try {
      if (!isBrowser()) return "srv";
      const key = "__trk_session";
      const existing = sessionStorage.getItem(key);
      if (existing) return existing;
      const id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${now()}-${Math.random()}`;
      sessionStorage.setItem(key, id);
      return id;
    } catch {
      return `${now()}`;
    }
  }

  private safePathname(): string {
    try {
      return isBrowser() ? location.pathname : "";
    } catch {
      return "";
    }
  }

  private safeReferrer(): string | null {
    try {
      return isBrowser() ? document.referrer || null : null;
    } catch {
      return null;
    }
  }
}

/* -------------------- Singleton & Public API -------------------- */

const g = globalThis as unknown as { __trk?: TrackClient };
const core: TrackClient = g.__trk ?? new TrackClient();
g.__trk = core;

/** Emit an event (auto-injects sessionId, ts, path, ref, schemaVersion) */
export function track(p0: string, p1: { label: string; component: string; }, ev: TrackEvent): void {
  core.enqueue(ev);
}

/** Manually flush (e.g., before route change) */
export function flush(): Promise<void> {
  return core.flush();
}

/** Update config (call once on app bootstrap) */
export function configure(partial: Partial<TrackConfig>): void {
  core.configure(partial);
}
